/* eslint-disable @typescript-eslint/no-explicit-any */
// @todo fix use of `any` in this file

import { useEffect, useId, useRef, useState } from 'react';

import { useMap } from '@/renderer/utils/hooks/useMap';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';

import { useDeepCompareEffectNoCheck, useDeepMemo } from './hooks/useDeepMemo';
import { useEffectOnce } from './hooks/useEffectOnce';
import { useLayoutEffectOrEffect } from './ssr';
import { useIdleContext } from './useIdle';
import { getConnection, wsPad } from './ws/connection';
import type { HookReturn, Subscriptions, WebSocketParams } from './ws/types';
import { ReadyState } from './ws/types';

export const WS_BASE_URL = wsPad('/api/v1/ws');

export default function useWebSocket<
  const T extends Record<string, unknown>,
  const V = T[keyof T],
  const R = V,
  const FromSocketType = any,
>(
  url: string = WS_BASE_URL,
  params: WebSocketParams<T, R, FromSocketType> = {},
): HookReturn<Omit<Map<keyof T, R>, 'set' | 'clear' | 'delete'>> {
  const {
    connect = true,
    onOpen,
    onReady,
    onMessage,
    onClose,
    onError,
    subscriptions: subscriptionsBase,
  } = params;

  const id = useId();

  // Keep track of mounting state of this component (useful in useEffect hooks)
  const isUnmounting = useRef(false);
  const isMounted = useRef(true);

  useLayoutEffectOrEffect(() => {
    isUnmounting.current = false;

    return () => {
      isUnmounting.current = true;
    };
  }, []);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Get a connection from the pool if it already exists
  // We don't do this here because it would cause SSR to attempt creating a WebSocket
  // const connection = getConnection(url, false);

  const [lastMessageRaw, _setLastMessageRaw] = useState<string | ArrayBuffer | null>(null);
  const [lastMessage, { set: _setLastMessage }] = useMap<keyof T, R>();

  // Setup first listeners as ref
  const listenersRef = useRef({
    message: onMessage,
    open: onOpen,
    ready: onReady,
    close: onClose,
    error: onError,
  });

  const subscriptions = useDeepMemo(subscriptionsBase);

  // Move subscriptions object to a ref
  const subscriptionsRef = useRef<Subscriptions<T, FromSocketType> | null>(null);

  // Memoize the subscriptions on first render
  if (subscriptionsRef.current === null) {
    subscriptionsRef.current = subscriptions ? { ...subscriptions } : {};
  }

  // Init the connection object into a ref
  const connRef = useRef(getConnection(url, false));

  const [reconnect, _setReconnect] = useState(() => connRef.current.reconnect);
  const [close, _setClose] = useState(() => connRef.current.close);
  const [sendMessage, _setSendMessage] = useState(() => connRef.current.sendMessage);
  const [readyState, setReadyState] = useState(connRef.current.readyState);

  // First time mounting
  useEffectOnce(() => {
    // Add subscribers
    connRef.current.mount(id, listenersRef.current, subscriptionsRef.current, setReadyState);

    // And start the WebSocket
    connRef.current = getConnection(url, connect);

    return () => {
      // Final unmount
      connRef.current.unmount(id, listenersRef.current, subscriptionsRef.current, setReadyState);
      subscriptionsRef.current = null;
    };
  });

  // Track when the subscriptions object changes, and modify subscription listeners if they have changed
  useDeepCompareEffectNoCheck(() => {
    if (subscriptions) {
      for (const key in subscriptions) {
        const cb = subscriptions[key];
        const oldCb = subscriptionsRef.current?.[key];

        // Check if this listener has changed
        if (cb !== oldCb) {
          if (oldCb) {
            // Remove old listener
            connRef.current.unlisten({ [key]: oldCb });
          }

          // Bind new one
          if (cb) {
            connRef.current.relisten({ [key]: cb });
          }

          // this is always true due to the memoization of subscriptionsRef
          if (subscriptionsRef.current) {
            subscriptionsRef.current[key] = cb;
          }
        }
      }

      return () => {
        // Only stop the listeners if this component is actually dismounting, and not just if the values change
        // if (subscriptionsRef.current && isUnmounting.current) {
        //   connRef.current.unlisten(subscriptionsRef.current);
        // }
        // subscriptionsRef.current = null;
      };
    }
  }, [subscriptions]);

  // Track when listeners change
  useUpdateEffect(() => {
    const newListeners = {
      message: onMessage,
      open: onOpen,
      ready: onReady,
      close: onClose,
      error: onError,
    };

    for (const key of ['close', 'error', 'message', 'open', 'ready'] as const) {
      const cb = newListeners[key];
      const oldCb = listenersRef.current[key];

      // Check if this listener has changed
      if (cb !== oldCb) {
        if (oldCb) {
          // Remove old listener
          connRef.current.unlisten({ [key]: oldCb });
        }

        // Bind new one
        connRef.current.relisten({ [key]: cb });

        listenersRef.current[key] = cb as any;
      }
    }

    return () => {
      for (const key of ['close', 'error', 'message', 'open', 'ready'] as const) {
        if (listenersRef.current[key]) {
          delete listenersRef.current[key];
        }
      }
    };
  }, [onClose, onError, onMessage, onOpen, onReady]);

  // Track endpoint url changes. Tell the connection we unmounted and start a new one
  // biome-ignore lint/correctness/useExhaustiveDependencies(id):
  // biome-ignore lint/correctness/useExhaustiveDependencies(connect):
  // biome-ignore lint/correctness/useExhaustiveDependencies(url):
  useUpdateEffect(() => {
    connRef.current.unmount(id, listenersRef.current, subscriptionsRef.current, setReadyState);

    connRef.current = getConnection(url, connect);

    connRef.current.mount(id, listenersRef.current, subscriptionsRef.current, setReadyState);
  }, [url]);

  // Track connection status changes. Start a new connection if needed
  // biome-ignore lint/correctness/useExhaustiveDependencies(id):
  // biome-ignore lint/correctness/useExhaustiveDependencies(url):
  useUpdateEffect(() => {
    connRef.current = getConnection(url, connect);

    if (connect) {
      connRef.current.mount(id, listenersRef.current, subscriptionsRef.current, setReadyState);
    } else {
      connRef.current.unmount(id, listenersRef.current, subscriptionsRef.current, setReadyState);
    }
  }, [connect]);

  const idleContext = useIdleContext();

  useUpdateEffect(() => {
    if (!idleContext.inactive && isMounted.current) {
      // We reactivated the app, make sure WS reconnects instantly if it lost connection
      // This is an attempted measure for iOS pushing stuff into the background, where they lose WS connections
      connRef.current?.reconnect?.();
    }
  }, [idleContext.inactive]);

  return {
    readyState,
    ready: readyState === ReadyState.READY,
    sendMessage,
    lastMessageRaw,
    lastMessage,
    reconnect,
    close,
  };
}
